import { useCallback } from 'react';
import { useApiQuery, useMesterplanApi } from '@mesterplan/web-hooks';
import type { ApiResult } from '@mesterplan/web-hooks';
import type { DpaDocumentDto, DpaStatusDto, MesterplanApi } from '@mesterplan/api-client';
import { useNetUser } from '@/hooks/useNetUser';

/**
 * The company's data processing agreement (DPA) status, `GET /api/companies/me/dpa`.
 *
 * Ships dark: while no DPA text is published the API answers `currentVersion: null` (an older
 * backend answers 404). `available` is false then, and every DPA element stays hidden.
 */
export function useDpaStatus() {
  const api = useMesterplanApi() as unknown as MesterplanApi;
  const { user, isLoading: userLoading } = useNetUser();
  const companyId = user?.activeOrgId ?? null;

  const query = useApiQuery<DpaStatusDto>(
    () =>
      (companyId
        ? api.dpa.getStatus()
        : Promise.resolve({ ok: true as const, status: 0, data: null })) as Promise<
        ApiResult<DpaStatusDto>
      >,
    [companyId],
  );

  const { refetch } = query;
  const status = query.data;
  const available = status !== null && status.currentVersion !== null;

  /** Accepts `version` on behalf of the company (settings.editCompany) and refreshes the status. */
  const accept = useCallback(
    async (version: string): Promise<ApiResult<DpaStatusDto>> => {
      const res = await api.dpa.accept({ version });
      if (res.ok) refetch();
      return res;
    },
    [api, refetch],
  );

  return {
    status,
    available,
    /** Still loading the first answer. */
    isLoading: userLoading || query.status === 'idle' || query.status === 'loading',
    /** A failure other than "no DPA published" (404). */
    isError: query.status === 'error' && query.error?.status !== 404,
    refetch,
    accept,
  };
}

export type CurrentDpaVersion =
  | { state: 'loading' }
  /** 404: nothing published. Company creation works as before. */
  | { state: 'dark' }
  | { state: 'ready'; version: string }
  /** The fetch failed for another reason: do not create a company without the tick. */
  | { state: 'error'; retry: () => void };

/**
 * The published DPA version for the signup checkbox. Anonymous, so it works before the user
 * has a company.
 */
export function useCurrentDpaVersion(): CurrentDpaVersion {
  const api = useMesterplanApi() as unknown as MesterplanApi;
  const query = useApiQuery<DpaDocumentDto>(() => api.dpa.getCurrentText(), [api]);
  if (query.status === 'success' && query.data) return { state: 'ready', version: query.data.version };
  if (query.status === 'error') {
    return query.error?.status === 404 ? { state: 'dark' } : { state: 'error', retry: query.refetch };
  }
  return { state: 'loading' };
}
